import { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AlertModal from "../../../Components/Modals/AlertModal";
import InputField from "../../../Components/InputField";
import CustomCheckboxLabel from "../../../Components/CustomCheckboxLabel";
import { useSnackbar } from "../../../Components/SnackBar";
import { aiErrorKey, useSavedAnnouncements } from "../hooks/useCommunityAi";
import AnnouncementCard from "./AnnouncementCard";
import { SavedListState, toolStyles } from "./ReplyHelper";
import { GREEN, g } from "../styles";

// Same limit as the API (COMY1-373 §6).
const MAX_BRIEF = 500;

// "Community message" tool, inside the page's AI column: the manager says what
// to announce in a few words, and gets a ready post to share with the groups.
const AnnouncementTool = ({ lang, t }) => {
  const { announcements, status, reload, ask, remove } = useSavedAnnouncements(lang);
  const snackbar = useSnackbar();
  const [brief, setBrief] = useState("");
  // On by default — the community groups include people who haven't joined.
  const [includeJoinLink, setIncludeJoinLink] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(announcements[0]?.id ?? null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const submit = async () => {
    if (!brief.trim()) {
      setError(t("announce.empty_error"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const announcement = await ask({ brief: brief.trim(), includeJoinLink, uiLang: lang });
      setExpandedId(announcement.id);
      setBrief("");
    } catch (err) {
      // Keep the brief, so "try again" is one click.
      setError(t(aiErrorKey(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={toolStyles.form}>
        <InputField
          value={brief}
          onChange={(e) => {
            setBrief(e.target.value);
            if (error) setError("");
          }}
          placeholder={t("announce.placeholder")}
          multiline
          rows={4}
          maxLength={MAX_BRIEF}
          disabled={loading}
        />

        <CustomCheckboxLabel
          label={t("announce.join_link")}
          checked={includeJoinLink}
          onChange={(e) => setIncludeJoinLink(e.target.checked)}
          disabled={loading}
        />

        {error && <Typography sx={toolStyles.error} role="alert">{error}</Typography>}

        <Box sx={toolStyles.submitRow}>
          <Button variant="contained" onClick={submit} disabled={loading} sx={g.primaryBtn}>
            {t("announce.submit")}
          </Button>
          {loading && (
            <>
              <CircularProgress size={18} sx={{ color: GREEN }} />
              <Typography component="span" sx={toolStyles.loading}>{t("announce.loading")}</Typography>
            </>
          )}
        </Box>
      </Box>

      <Typography component="h3" sx={toolStyles.savedHeader}>
        {t("announce.saved_title", { count: announcements.length })}
      </Typography>

      {status !== "ready" ? (
        <SavedListState status={status} onRetry={reload} t={t} />
      ) : announcements.length === 0 ? (
        <Typography sx={toolStyles.savedEmpty}>{t("announce.saved_empty")}</Typography>
      ) : (
        <Box sx={toolStyles.savedList}>
          {announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              lang={lang}
              t={t}
              expanded={expandedId === announcement.id}
              onToggle={() => setExpandedId((id) => (id === announcement.id ? null : announcement.id))}
              onDelete={() => setPendingDelete(announcement.id)}
            />
          ))}
        </Box>
      )}

      <AlertModal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onActionClick={async () => {
          const id = pendingDelete;
          setPendingDelete(null);
          try {
            await remove(id);
          } catch {
            snackbar(t("ai.errors.delete_failed"), "error");
          }
        }}
        title={t("announce.delete_title")}
        description={t("announce.delete_body")}
        actionBtnText={t("helper.delete")}
      />
    </Box>
  );
};

export default AnnouncementTool;
